import React, { useContext, useMemo } from "react";
import "./TicketingSystemDetailsInstanceRow.scss";
import { useTranslation } from "react-i18next";
import { TextButton } from "@SeemplicityDev/shared";
import { Link, useRouteMatch } from "react-router-dom";
import {
  AutomationSystemsContext,
  type AutomationSystemsContextType,
} from "../AutomationSystemsWrapper/AutomationSystemsProvider";

export interface TicketingSystemInstance {
  id?: string;
  known_identifier?: string;
  is_installed?: boolean;
  supports_editing?: boolean;
}

export interface TicketingSystemDetailsInstanceRowProps {
  instance: TicketingSystemInstance;
}

const TicketingSystemDetailsInstanceRow: React.FC<
  TicketingSystemDetailsInstanceRowProps
> = ({ instance }) => {
  const { url } = useRouteMatch();

  const { editTicketingSystemAllowed } = useContext(
    AutomationSystemsContext
  ) as AutomationSystemsContextType;

  const { t } = useTranslation();

  const { disabled, tooltipContent } = useMemo(() => {
    const supportsEditingVal = instance.supports_editing !== false;

    // Scenario 1: User is not an admin
    if (!editTicketingSystemAllowed) {
      return {
        disabled: true,
        tooltipContent: "You don’t have permission to perform this action",
      };
    }

    // Scenario 2: Admin, but provider doesn't support editing
    if (!supportsEditingVal) {
      return {
        disabled: true,
        tooltipContent: "Editing this provider type is not yet supported.",
      };
    }

    // Scenario 3: All clear
    return {
      disabled: false,
      tooltipContent: undefined,
    };
  }, [instance.supports_editing, editTicketingSystemAllowed]);

  const getLinkedTitle = (title: string) => (
    <Link to={`${url}/${instance.id}`}>
      <TextButton
        wrapperClassName={"ts-instance-details"}
        data-testid={"ts-instance-details"}
        type={"button"}
        label={title}
        buttonStyle={"textLink"}
        disabled={disabled}
        withTooltip={tooltipContent ? { content: tooltipContent } : undefined}
        onClick={((e: React.MouseEvent) => {
          if (disabled) {
            e.stopPropagation();
          }
          // TextButtonProps types onClick as `() => void`, but the underlying
          // button forwards the native event; cast preserves runtime behavior.
        }) as unknown as () => void}
      />
    </Link>
  );

  return (
    <div className={"ts-instance-wrapper"}>
      <div
        className={"ts-instance-cell ts-instance-name"}
        title={instance.known_identifier}
      >
        {instance.known_identifier}
      </div>
      <div className={"ts-instance-cell ts-instance-sync"}>
        {getLinkedTitle(
          instance.is_installed ? t("Installed") : t("Continue Installation")
        )}
      </div>
    </div>
  );
};

export default TicketingSystemDetailsInstanceRow;
